'use client';

import Image from 'next/image';
import { Suspense } from 'react';
import TableOfContents from '@/components/TableOfContent/TableOfContents';
import ProcessCard from '@/components/Cards/ProcessCard/ProcessCard';
import BenefitCard from '@/components/Cards/BenefitCard/BenefitCard';
import RecognitionCard from '@/components/Cards/RecognitionCard/RecognitionCard';
import BottomBanner from '@/components/BannerSection/BottomBanner/BottomBanner';
import ChampionBanner from '@/components/BannerSection/ChampionBanner';
import AnalyticsTracker from '@/analytics/AnalyticsTracker';
import ContentEngagementClient from '@/analytics/ContentEngagementClient';
import { cleanMetadata } from '@/utils/cleanMetadata';
import { thisHostAndBasePath } from '@/../config';
import './program-overviews.scss';

export default function ChampionsOverviewPage() {
  const pageMetaData = cleanMetadata({
    slug: 'champion/program-overview',
    publish_date: null,
    date_modified: null,
    url: `${thisHostAndBasePath}/community/champions-program/`,
    title: 'IBM Champions Program Overview & Benefits',
    favourite: '',
    like: '',
    follow: '',
    status: '',
    primary_category: '',
    tags: '',
    strategic_content_area: '',
    authors: '',
    authors_email: '',
    editors: '',
    editors_email: '',
    record_type: 'static-page',
    secondary_tag: '',
  });

  const navigationItems = [
    { label: 'Who are Champions?', href: '#who-are-champions' },
    { label: 'What does it take to become a champion?', href: '#what-does-it-take' },
    { label: 'How the program works', href: '#how-program-works' },
    { label: 'Benefits of the program', href: '#benefits-of-program' },
    { label: 'Program recognition', href: '#program-recognition' },
  ];

  return (
    <div className="champions-page">
      <Suspense fallback={null}>
        <AnalyticsTracker />
      </Suspense>
      <ContentEngagementClient metaData={pageMetaData} />

      {/* HERO */}
      <section className="">
        <ChampionBanner
          title="IBM Champions program overview & benefits"
          subtitle="IBM celebrates top advocates and community leaders"
          imageUrl="https://dw1.s81c.com/developer-static-pages/open/en/home/images/program-overviews.png"
          imageAlt="IBM Developer Champion - Start your journey from learner to leader"
        />
      </section>

      {/* MAIN LAYOUT */}
      <div className="champions-container">
        {/* SIDEBAR */}
        <aside className="sticky-sidebar">
          <TableOfContents
            navigationItems={navigationItems}
            hideTitle={true}
            defaultActiveFirst={true}
          />
        </aside>

        {/* CONTENT */}
        <main className="champions-content-section">
          <section className="champions-intro">
            <p className="intro-text">
              The IBM Champions programs are represented by a global community of the most
              passionate, loyal, and talented IBM customers and partners: they are advocates,
              experts, and thought leaders.
            </p>
          </section>
          <hr className="section-divider" />
          <section className="content-section">
            <h2 id="who-are-champions">Who are Champions?</h2>
            <p>
              Champions are at the pinnacle of expertise and advocacy: these individuals are
              exceptional in their commitment to paying forward their knowledge and experiences.
              They share with the larger technical community in the hopes of guiding buying
              decisions, inspiring innovation, shaping IBM product development, educating new
              generations of users, and deepening community growth.
              <br />
              <br />
            </p>
            <p></p>
            <p>
              Each one combines deep knowledge across IBM products and technologies with the drive
              to help and support others.
              <br />
              <br />
            </p>
            <div className="champions-image-placeholder">
              <Image
                src="https://dw1.s81c.com/developer-static-pages/open/en/home/images/ibm-champions-overview-benefits-hero.jpg"
                alt="IBM Champions community members"
                width={600}
                height={338}
                priority
                fetchPriority="high"
                quality={75}
                className="champions-image"
              />
            </div>
          </section>
          <hr className="section-divider" />
          <section className="content-section">
            <h2 id="what-does-it-take">What does it take to become a Champion?</h2>
            <p>
              Expertise in IBM technology and the drive to share your knowledge to help others.
              That’s what it takes to join.
              <br />
              <br />
            </p>
            <p>
              Demonstrate your technical talent and your enthusiasm to share knowledge with others
              in one or more of these activities: speaking at conferences and events; supporting
              <a href="https://www.ibm.com/community/user-groups/"> user groups</a> and community;
              creating articles, videos, and other spotlights; providing product feedback to IBM
              development teams; mentoring and teaching upcoming technologists; or supporting IBM
              with references and reviews.
              <br />
              <br />
            </p>
            <p>
              If this is work you’re already doing, then you could be on the pathway to a future
              Champion nomination.
              <br />
              <br />
            </p>
          </section>

          <hr className="section-divider" />

          <section className="content-section">
            <h2 id="how-program-works">How the program works</h2>

            <div className="process-card-grid">
              <ProcessCard
                image="https://dw1.s81c.com/developer-static-pages/open/en/home/images/Pictogram.png"
                title="Advocacy"
                description="You (or someone you know) are already doing the work: creating content, speaking at conferences, starting and running user groups, and much more."
              />

              <ProcessCard
                image="https://dw1.s81c.com/developer-static-pages/open/en/home/images/Pictogram-submit.png"
                title="Submit"
                description="You submit an application (or a referral) that gives us the details and evidence about your expertise and all the great advocacy work you are doing."
              />

              <ProcessCard
                image="https://dw1.s81c.com/developer-static-pages/open/en/home/images/Pictogram-eye.png"
                title="Review"
                description="The program reviews submissions year round to issue advocacy badges. During evaluation season, we also review candidates for Champion status."
              />

              <ProcessCard
                image="https://dw1.s81c.com/developer-static-pages/open/en/home/images/Pictogram-select.png"
                title="Select"
                description="The committees review and select eligible candidates for badges. Top advocates may earn Champion status based on their ongoing and exceptional contributions."
              />

              <ProcessCard
                image="https://dw1.s81c.com/developer-static-pages/open/en/home/images/Pictogram-announce.png"
                title="Announce"
                description="The new class of Champions is named each new year. Advocacy badges are issued monthly and may be earned annually."
              />

              <ProcessCard
                image="https://dw1.s81c.com/developer-static-pages/open/en/home/images/Pictogram-star.png"
                title="All year"
                description="You might be a future Champion, but you don't have to wait until evaluation season. You can get started with the IBM Rising Champions Advocacy badge program."
              />
            </div>
          </section>

          <hr className="section-divider" />

          <section className="content-section">
            <h2 id="benefits-of-program">Benefits of the program</h2>

            <div className="benefits-intro">
              <p>
                <span className="strong">Picture yourself here</span>
              </p>
              <p className="benefits-intro-text">
                From enhanced and highlighted profiles to VIP treatment at events, Champions earn
                recognition, gratitude, and rewards for their advocacy contributions.
              </p>
            </div>
            <div className="benefits-image-container">
              <div className="benefits-image-placeholder">
                <Image
                  src="https://dw1.s81c.com/developer-static-pages/open/en/home/images/benefitImg.jpeg"
                  alt="IBM Champions program benefits"
                  width={600}
                  height={0}
                  className="benefits-image"
                  style={{ height: 'auto' }}
                />
              </div>
            </div>
            <br />
            <p className="benefits-membership-text">
              Membership in the program also earns important benefits, designed to enhance
              professional development and eminence.
            </p>

            <div className="benefits-card-grid">
              <BenefitCard
                title="Access to pre-release product briefings and advocacy opportunities with IBM"
                description="Champions programs offer a range of connection opportunities with our product and development teams including product briefings, opportunities to join advisory boards, or provide new ideas to product enhancements."
              />

              <BenefitCard
                title="Conferences and events"
                description="Champions are the VIP guests at our flagship conferences, with complimentary passes, special gifts (have you seen the jackets?!), and exclusive experiences. Of course, you'll also recieve invitations and discounts to other IBM and industry events worldwide."
              />

              <BenefitCard
                title="Learning and certifications"
                description="Keep your skills and expertise in good shape with discounts and complimentary access to IBM learning and certifications."
              />

              <BenefitCard
                title="Gifts and gear"
                description="Show off your status and get recognized at industry and IBM conferences, user group meet-ups, and sometimes in airports, with our unique logo gear."
              />
            </div>
          </section>

          <hr className="section-divider" />

          <section className="content-section">
            <h2 id="program-recognition">Program recognition</h2>
            <p className="recognition-intro">
              IBM Champions promote their status by sharing digital badges with their social
              networks, in their blogs, and on their websites. These industry recognized credentials
              are issued by Credly, independently verified, and industry reognized. Other
              opportunities to highlight their status include ribbons on community profiles,
              spotlight interviews, sharing their content, and more.
              <br />
            </p>
            <p className="recognition-intro">
              We celebrate the highest achievements of technical experts, who have contributed their
              time and talent to advancing the technical community. They are selected for their
              demonstrated commitment to 'paying it forward' through their extensive acts of
              advocacy over the previous year.
              <br />
            </p>

            <div className="recognition-cards-grid ">
              <RecognitionCard
                badgeImageAlt="IBM Champion 2026 badge"
                badgeImage="https://dw1.s81c.com/developer-static-pages/open/en/home/images/Champions-2026.png"
                title="Champion 2026"
                description="IBM recognizes exceptional individuals who have contributed their time, talent, and expertise to the advancement of the technical community via a multitude of value-add advocacy activities."
                learnMoreLink="https://community.ibm.com/community/user/champions/blogs/kathryn-schwab1/2024/07/12/ibm-champion-program-badges"
              />

              <RecognitionCard
                badgeImageAlt="IBM Champion Lifetime Achievement badge"
                badgeImage="https://dw1.s81c.com/developer-static-pages/open/en/home/images/ibm-champoins-lifetime-achievement.png"
                title="Champion Lifetime Achievement"
                description="We also celebrate IBM Lifetime Champions with a special badge and designation dedicated to recognizing their exemplary service to the community and indicating they are a Champion for life."
                learnMoreLink="https://community.ibm.com/community/user/champions/blogs/kathryn-schwab1/2024/07/12/ibm-champion-program-badges"
              />
            </div>
            <br />
            <br />
            <p className="recognition-footer">
              All Champions receive the digital credential and may receive additional milestone
              recognition at the 3, 5, 7, 10, and 15 year renewals, which are verified digital
              credentials issued through our{' '}
              <a
                href="https://community.ibm.com/community/user/champions/blogs/kathryn-schwab1/2024/07/12/ibm-champion-program-badges"
                target="_blank"
                rel="noopener"
              >
                {' '}
                Digital Badge program
              </a>{' '}
              and Credly.
              <br />
              <br />
            </p>

            <div className="milestone-badges-section">
              <div className="milestone-timeline">
                <div className="timeline-badge">
                  <Image
                    src="https://dw1.s81c.com/developer-static-pages/open/en/home/images/ibm-benefits-overview-program-recognition.png"
                    alt="IBM Champion Lifetime Achievement badge"
                    width={786}
                    height={442}
                  />
                </div>
              </div>
            </div>

            <p className="rising-champions-text">
              <br />
              <br />
              Interested in starting your pathway to become a Champion? You can get started by
              qualifying through the{' '}
              <a href="https://community.ibm.com/community/user/blogs/kathryn-schwab1/2026/05/22/introducing-the-next-evolution-of-the-ibm-rising-c">
                {' '}
                IBM Rising Champions Advocacy badge program{' '}
              </a>{' '}
              .
              <br />
              <br />
            </p>
          </section>
          <hr className="section-divider" />
        </main>
      </div>

      <BottomBanner
        title="Find your path in the advocacy community"
        subHeader="Whether you're an emerging advocate or an established thought leader, there’s a place for you in our global community."
        ctas={[
          {
            label: 'New? Start your Rising Champion application',
            href: 'https://ibm.biz/apply_AdvocacyBadge',
          },
          {
            label: 'Part of the program? Report your activities & earn badges',
            href: 'https://ibm.biz/champ-report',
          },
        ]}
        learnMoreText="Learn more"
        learnMoreLink="https://community.ibm.com/community/user/blogs/kathryn-schwab1/2026/05/22/introducing-the-next-evolution-of-the-ibm-rising-c"
        backgroundColor="#f4f4f4"
      />
    </div>
  );
}
